import { Component, inject, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import {
	AdyenCheckout,
	CoreConfiguration,
	GooglePay,
	GooglePayConfiguration,
	SubmitActions,
	SubmitData,
	UIElement,
} from '@adyen/adyen-web';
import Order from '../../../smoothr-web-app-core/models/Order';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { AdyenCredentials } from '../../../smoothr-web-app-core/models/Venue';
import { PaySessionStatistics } from '../../../smoothr-web-app-core/models/PaySessionStatistics';
import { environment } from '../../../environments/environment';

export interface GooglePayModalResult {
	googlePayToken: string;
	submitActions: SubmitActions;
}

@Component({
	standalone: false,
	selector: 'app-google-pay-modal',
	templateUrl: './google-pay-modal.component.html',
	styleUrls: ['./google-pay-modal.component.scss'],
})
export class GooglePayModalComponent implements OnInit {
	@Input() order: Order | null = null;

	totalPrice = 0;
	formattedTotalPrice = '';
	formattedTips = '';
	paySessionStatistics: PaySessionStatistics | null = null;

	loading = true;

	private repositoryService = inject(RepositoryService);
	private adyenCredentials: AdyenCredentials | null = null;

	private modalCtrl: ModalController = inject(ModalController);
	private googlePayComponent: GooglePay | null = null;

	constructor() {}

	get isValid(): boolean {
		if (!this.googlePayComponent) {
			return false;
		}
		return this.googlePayComponent.isValid;
	}

	get formattedSelected() {
		return this.repositoryService.currencyFormat.format(this.paySessionStatistics?.selectedTotalPrice ?? 0);
	}

	static async show(
		modalCtrl: ModalController,
		paySessionStatistics: PaySessionStatistics | null,
		order: Order,
		adyenCredentials: AdyenCredentials
	): Promise<GooglePayModalResult> {
		const modal = await modalCtrl.create({
			component: GooglePayModalComponent,
			cssClass: 'payment-sheet-modal',
			componentProps: {
				order,
				itemCount: order.checkoutOrderedArticles.length,
				adyenCredentials,
				paySessionStatistics,
			},
		});

		await modal.present();
		const { data } = await modal.onDidDismiss();
		return data;
	}

	ngOnInit() {
		this.loadPayment();
	}

	async loadPayment() {
		await this.paymentMethodGooglePayAdyen();
		this.loading = false;
	}

	async paymentMethodGooglePayAdyen() {
		this.loading = true;
		this.reloadPrices();
		const prevGooglePayComponent = this.googlePayComponent;
		if (prevGooglePayComponent) {
			prevGooglePayComponent.unmount();
		}

		const googleConfig: GooglePayConfiguration = {
			buttonType: 'checkout',
			buttonColor: 'white',
			environment: environment.googlePayEnvironment,
			configuration: {
				merchantId: environment.googlePayMerchant,
				gatewayMerchantId: this.adyenCredentials!!.merchantAccount,
				merchantName: environment.googlePayMerchantName,
			},
			amount: {
				value: this.totalPrice * 100,
				currency: 'EUR',
			},
		};
		console.log({ googleConfig });
		const modal = this;
		const confirmAdyenForm = this.confirm;

		const configuration: CoreConfiguration = {
			locale: 'de_DE',
			environment: environment.adyenEnvironment as 'test' | 'live' | 'live-us' | 'live-au' | 'live-apse' | 'live-in',
			clientKey: this.adyenCredentials?.clientKey,
			countryCode: 'DE',
			analytics: {
				enabled: false, //disable analytics cors errors
			},
			amount: {
				value: this.totalPrice,
				currency: 'EUR',
			},
			onSubmit: async (state: SubmitData, component: UIElement, action: SubmitActions) => {
				console.log('state', state, component, action);
				confirmAdyenForm(modal, state, action);
			},
			onPaymentCompleted: (result, component) => {
				console.log('✅ Payment completed:', result, component);
			},
			onError: (error, component) => {
				console.error('❌ Payment error:', error, component);
			},
		};
		console.log({ configuration });
		const paymentMethods = await this.repositoryService.getAdyenPaymentMethods(this.order!.venue);
		console.log({
			adyenCredentials: this.adyenCredentials,
			googleConfig,
			configuration,
			paymentMethods,
		});
		const checkout = await AdyenCheckout(configuration);
		const googlePayComponent = new GooglePay(checkout, googleConfig);
		this.googlePayComponent = googlePayComponent;
		try {
			await this.googlePayComponent.isAvailable();
			googlePayComponent.mount('#google-pay-container');
		} catch (e) {
			console.error('Google Pay is not available', e);
		}
		this.reloadPrices();
		this.loading = false;
	}

	async confirm(modal: GooglePayModalComponent, submitData: SubmitData, actions: SubmitActions) {
		if (!submitData || !submitData.isValid || !submitData.data.paymentMethod['googlePayToken']) {
			console.error('No Adyen data found or not valid', submitData);
			return;
		}
		const googlePayToken = submitData.data.paymentMethod['googlePayToken'];
		await modal.modalCtrl.dismiss({
			googlePayToken,
			submitActions: actions,
		});
	}

	async confirmButton() {
		const googlePayComponent = this.googlePayComponent;
		if (!googlePayComponent) {
			console.error('No cardCheckout found');
			return;
		}
		if (!googlePayComponent.isValid) {
			console.error('Card is not valid');
			return;
		}
		googlePayComponent.submit();
	}

	reloadPrices() {
		const selectedTotalPrice = this.paySessionStatistics?.selectedTotalPrice ?? 0;
		const tipValue = this.paySessionStatistics?.tipsValue ?? 0;
		this.totalPrice = selectedTotalPrice;
		this.formattedTotalPrice = this.repositoryService.currencyFormat.format(this.totalPrice);
		this.formattedTips = this.repositoryService.currencyFormat.format(tipValue);
	}

	close() {
		this.modalCtrl.dismiss();
	}
}
